import React, { useState, useRef, useEffect } from 'react';
import './MessageInput.css';

const MessageInput = ({ onSendMessage, disabled, currentChat, onFilterChange }) => {
  const [message, setMessage] = useState('');
  const [useCustomAES, setUseCustomAES] = useState(() => {
    const saved = localStorage.getItem('toxchat_aes_enabled');
    return saved === 'true';
  });
  const [aesPassword, setAesPassword] = useState(() => {
    const saved = localStorage.getItem('toxchat_aes_password');
    return saved || '';
  });
  const [customTag, setCustomTag] = useState(() => {
    const saved = localStorage.getItem('toxchat_aes_tag');
    return saved || '';
  });
  const [onlyMessageTag, setOnlyMessageTag] = useState(false);
  const [showAESOptions, setShowAESOptions] = useState(false);
  const textareaRef = useRef(null);

  // RSA 2048位密钥的最大加密长度（175字节，兼容自定义AES加密）
  const RSA_MAX_LENGTH = 175;
  const isOverLimit = message.length > RSA_MAX_LENGTH;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!message.trim() || disabled || isOverLimit) return;

    onSendMessage({
      content: message.trim(),
      useCustomAES,
      aesPassword: useCustomAES ? aesPassword : null,
      customTag: useCustomAES ? customTag : null
    });

    setMessage('');
    // 只有在不使用自定义AES加密时才清空密码和标签
    if (!useCustomAES) {
      setAesPassword('');
      setCustomTag('');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const toggleAESOptions = () => {
    setShowAESOptions(!showAESOptions);
    if (!showAESOptions) {
      setUseCustomAES(false);
      setOnlyMessageTag(false);
    }
  };

  const handleOnlyMessageTagChange = (e) => {
    const checked = e.target.checked;
    setOnlyMessageTag(checked);
  };

  // 当过滤设置改变时，通知父组件
  useEffect(() => {
    // 保存设置到本地存储
    localStorage.setItem('toxchat_aes_enabled', useCustomAES.toString());
    localStorage.setItem('toxchat_aes_password', aesPassword);
    localStorage.setItem('toxchat_aes_tag', customTag);
    localStorage.setItem('toxchat_only_message_tag', onlyMessageTag.toString());

    if (onFilterChange) {
      const newSettings = {
        enabled: useCustomAES && aesPassword && customTag,
        password: useCustomAES ? aesPassword : '',
        tag: useCustomAES ? customTag : '',
        onlyMessageTag: onlyMessageTag
      };
      
      onFilterChange(newSettings);
    }
  }, [useCustomAES, onlyMessageTag, aesPassword, customTag, onFilterChange]);

  // 组件挂载时立即通知父组件当前设置
  useEffect(() => {
    const timer = setTimeout(() => {
      if (onFilterChange) {
        const mountSettings = {
          enabled: useCustomAES && aesPassword && customTag,
          password: useCustomAES ? aesPassword : '',
          tag: useCustomAES ? customTag : '',
          onlyMessageTag: onlyMessageTag
        };
        
        onFilterChange(mountSettings);
      }
    }, 100);
    
    return () => clearTimeout(timer);
  }, [useCustomAES, aesPassword, customTag, onFilterChange]); // 添加必要的依赖

  const handleAESChange = (e) => {
    const checked = e.target.checked;
    setUseCustomAES(checked);
    
    // 如果禁用AES，也禁用消息过滤
    if (!checked) {
      setOnlyMessageTag(false);
      if (onFilterChange) {
        onFilterChange({ enabled: false, password: '', tag: '' });
      }
    } else {
      // 如果启用AES，立即触发过滤设置更新
      if (onFilterChange) {
        onFilterChange({
          enabled: checked && aesPassword && customTag,
          password: checked ? aesPassword : '',
          tag: checked ? customTag : ''
        });
      }
    }
  };

  return (
    <div className="message-input-container">
      {showAESOptions && (
        <div className="aes-options">
          <div className="aes-header">
            <div className="aes-checkboxes">
              <label className="aes-checkbox">
                <input
                  type="checkbox"
                  checked={useCustomAES}
                  onChange={handleAESChange}
                />
                Enable Custom Encryption
              </label>
              <label className="aes-checkbox">
                <input
                  type="checkbox"
                  checked={onlyMessageTag}
                  onChange={handleOnlyMessageTagChange}
                  disabled={!useCustomAES}
                />
                Only Message Tag
              </label>
            </div>
            <span className="aes-hint">ToxChat Custom Encryption</span>
          </div>
          
          {useCustomAES && (
            <div className="aes-inputs">
               <div className="input-group">
                 <label>Encryption Password:</label>
                 <input
                   type="password"
                   value={aesPassword}
                   onChange={(e) => {
                     setAesPassword(e.target.value);
                   }}
                   placeholder="Enter encryption password..."
                   className="aes-input"
                 />
               </div>
               <div className="input-group">
                 <label>Message Tag:</label>
                 <input
                   type="text"
                   value={customTag}
                   onChange={(e) => {
                     setCustomTag(e.target.value);
                   }}
                   placeholder="Custom tag (optional)"
                   className="aes-input"
                   maxLength={20}
                 />
               </div>
            </div>
          )}
        </div>
      )}

      <form onSubmit={handleSubmit} className="message-form">
        <div className="input-row">
          <button
            type="button"
            className="aes-toggle-btn"
            onClick={toggleAESOptions}
            title="Custom Encryption Options"
          >
            🔐
          </button>
          
          <textarea
            ref={textareaRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={disabled ? "Connecting..." : `Type message... (Enter to send, Shift+Enter for new line, Max: ${RSA_MAX_LENGTH} chars)`}
            disabled={disabled}
            className={`message-textarea ${isOverLimit ? 'over-limit' : ''}`}
            rows={1}
            maxLength={RSA_MAX_LENGTH + 50} // 允许稍微超过一点以便显示警告
          />
          
          <button
            type="submit"
            disabled={disabled || !message.trim() || isOverLimit}
            className="send-button"
          >
            Send
          </button>
        </div>
      </form>

      <div className="input-footer">
        <span className="encryption-status">
          {currentChat === 'public' ? 'Server Public Key Encryption' : 'End-to-End Encryption'}
          {useCustomAES && ' + ToxChat Custom Encryption'}
          {onlyMessageTag && ' + Tag Filter'}
        </span>
        <span className={`char-count ${isOverLimit ? 'over-limit' : ''}`}>
          {message.length}/{RSA_MAX_LENGTH}
          {isOverLimit && <span className="limit-warning"> Limit Exceeded!</span>}
        </span>
      </div>
    </div>
  );
};

export default MessageInput;